import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bell, BellOff, Sparkles } from "lucide-react";

const ENABLED_KEY = "em_journal_reminder_enabled";
const LAST_NOTIFIED_KEY = "em_journal_reminder_last";
const JOURNAL_KEY = "em_journal_entries_v1";
const REMINDER_HOUR = 20; // 20:00 local

function todayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function hasEntryToday() {
  try {
    const raw = localStorage.getItem(JOURNAL_KEY);
    if (!raw) return false;
    const data = JSON.parse(raw);
    const e = data[todayIso()];
    return !!(e && e.mood);
  } catch {
    return false;
  }
}

function supportsNotif() {
  return typeof window !== "undefined" && "Notification" in window;
}

export default function JournalReminder() {
  const [enabled, setEnabled] = useState(() => localStorage.getItem(ENABLED_KEY) === "true");
  const [permission, setPermission] = useState(supportsNotif() ? Notification.permission : "unsupported");
  const [bannerVisible, setBannerVisible] = useState(false);

  useEffect(() => {
    // Show banner if no entry today AND we're after REMINDER_HOUR (or anytime if enabled & not fired today)
    const now = new Date();
    const last = localStorage.getItem(LAST_NOTIFIED_KEY);
    const already = last === todayIso();
    const noEntry = !hasEntryToday();
    const afterHour = now.getHours() >= REMINDER_HOUR;
    if (noEntry && (afterHour || enabled) && !already) {
      setBannerVisible(true);
    }
  }, [enabled]);

  // Fire native notification if enabled, permitted, not yet fired today, after REMINDER_HOUR
  useEffect(() => {
    if (!enabled || permission !== "granted" || !supportsNotif()) return;
    const check = () => {
      const now = new Date();
      const last = localStorage.getItem(LAST_NOTIFIED_KEY);
      if (last === todayIso()) return;
      if (now.getHours() < REMINDER_HOUR) return;
      if (hasEntryToday()) return;
      try {
        new Notification("L'Équipe & Mental", {
          body: "Et toi aujourd'hui, comment tu te sens ? Une ligne, c'est déjà beaucoup.",
          icon: "/favicon.ico",
          tag: "em-journal-reminder",
        });
        localStorage.setItem(LAST_NOTIFIED_KEY, todayIso());
      } catch {}
    };
    check();
    const t = setInterval(check, 60_000);
    return () => clearInterval(t);
  }, [enabled, permission]);

  const toggle = async () => {
    if (!enabled) {
      if (supportsNotif() && Notification.permission === "default") {
        try {
          const p = await Notification.requestPermission();
          setPermission(p);
          if (p !== "granted") {
            toast("Rappel actif dans le navigateur (sans notification système).");
          }
        } catch {}
      } else if (supportsNotif()) {
        setPermission(Notification.permission);
      }
      localStorage.setItem(ENABLED_KEY, "true");
      setEnabled(true);
      toast.success("Rappel quotidien activé. On te fera un signe le soir.");
    } else {
      localStorage.setItem(ENABLED_KEY, "false");
      setEnabled(false);
      toast("Rappel désactivé.");
    }
  };

  const dismiss = () => {
    setBannerVisible(false);
    localStorage.setItem(LAST_NOTIFIED_KEY, todayIso());
  };

  const writeNow = () => {
    setBannerVisible(false);
    const section = document.querySelector("#journal [data-testid='journal-editor']");
    if (section) section.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  return (
    <>
      {bannerVisible && (
        <div
          className="mb-6 em-card p-5 flex items-start sm:items-center gap-4 flex-wrap"
          style={{
            background:
              "linear-gradient(90deg, rgba(249, 115, 22, 0.12), rgba(16, 185, 129, 0.1))",
          }}
          data-testid="journal-reminder-banner"
        >
          <div className="w-10 h-10 shrink-0 rounded-xl bg-orange-500/20 border border-orange-500/30 grid place-items-center">
            <Sparkles className="w-5 h-5 text-orange-300" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white font-semibold">Pas encore d'entrée aujourd'hui.</p>
            <p className="text-sm text-slate-300">
              Une seule ligne, c'est déjà suffisant. Comment tu te sens, là, maintenant ?
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={writeNow}
              data-testid="journal-reminder-write"
              className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold px-4 py-2 rounded-full text-sm"
            >
              Écrire maintenant
            </button>
            <button
              onClick={dismiss}
              data-testid="journal-reminder-dismiss"
              className="text-xs text-slate-400 hover:text-slate-200 px-2"
            >
              Plus tard
            </button>
          </div>
        </div>
      )}

      <div className="mb-6 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-sm text-slate-400">
          {enabled ? <Bell className="w-4 h-4 text-emerald-300" /> : <BellOff className="w-4 h-4" />}
          <span>
            Rappel quotidien à {REMINDER_HOUR}h — {" "}
            <span className={enabled ? "text-emerald-300" : "text-slate-500"}>
              {enabled ? "activé" : "désactivé"}
            </span>
          </span>
          {permission === "denied" && (
            <span className="text-xs text-rose-300">(notifications bloquées dans le navigateur)</span>
          )}
        </div>
        <button
          onClick={toggle}
          data-testid="journal-reminder-toggle"
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border ${
            enabled
              ? "bg-emerald-500/15 border-emerald-500/30 text-emerald-200 hover:bg-emerald-500/25"
              : "bg-white/5 border-white/10 text-slate-200 hover:bg-white/10"
          }`}
        >
          {enabled ? "Désactiver le rappel" : "Me rappeler chaque jour"}
        </button>
      </div>
    </>
  );
}
